import type { ReactNode } from "react";
import { connect } from "react-redux";
import { FormName, formValueSelector } from "redux-form/immutable";

interface LegacyFormEntry {
  values?: unknown;
}

interface LegacyFormSelectorState {
  form: Record<string, LegacyFormEntry>;
}

type BoundValueSelector = (...fieldNames: string[]) => unknown;

type CustomValueSelector = (
  selectValue: BoundValueSelector,
  form: string,
  sectionPrefix: string | undefined,
  values: unknown
) => unknown;

interface SharedSelectorProps {
  children(values: unknown): ReactNode;
  form?: string;
  sectionPrefix?: string;
}

interface FieldSelectorProps extends SharedSelectorProps {
  fieldName: string;
  selector?: undefined;
}

interface CustomSelectorProps extends SharedSelectorProps {
  fieldName?: string;
  selector: CustomValueSelector;
}

export type FormValueSelectorProps = FieldSelectorProps | CustomSelectorProps;

type ConnectedSelectorProps = FormValueSelectorProps & {
  form: string;
};

interface SelectedValueProps {
  values: unknown;
}

function FormValueSelectorView(props: FormValueSelectorProps & SelectedValueProps) {
  const { children, values } = props;

  return children(values) || null;
}

const mapStateToProps = (
  state: LegacyFormSelectorState,
  props: ConnectedSelectorProps
): SelectedValueProps => {
  const { form, sectionPrefix, fieldName, selector } = props;
  const valueSelector = formValueSelector<LegacyFormSelectorState>(form);

  if (typeof selector === "function") {
    const valueSelectorBoundToState: BoundValueSelector = (...fieldNames) =>
      valueSelector(state, ...fieldNames);

    return {
      values: selector(
        valueSelectorBoundToState,
        form,
        sectionPrefix,
        form in state.form ? state.form[form].values : {}
      )
    };
  }

  return {
    values: valueSelector(state, sectionPrefix ? `${sectionPrefix}.${fieldName}` : fieldName)
  };
};

const ConnectedFormValueSelector = connect<
  SelectedValueProps,
  Record<string, never>,
  ConnectedSelectorProps,
  LegacyFormSelectorState
>(mapStateToProps)(FormValueSelectorView);

export default function FormValueSelectorContextContainer(props: FormValueSelectorProps) {
  return (
    <FormName>
      {({ form, sectionPrefix }) => (
        <ConnectedFormValueSelector
          {...props}
          form={props.form || form}
          sectionPrefix={props.sectionPrefix || sectionPrefix}
        />
      )}
    </FormName>
  );
}
